/**
 * Binds the pure hash router (`hash.ts`) to `location.hash` and exposes the
 * current {@link Route} + a `navigate` via context. The only "route" in this
 * companion-first surface is which girlfriend is active, so this stays tiny — no
 * router dependency, deep-linkable, reload-safe. Listens to `hashchange` (other
 * tabs / back button) and cleans up with the owner.
 */
import { createContext, createSignal, onCleanup, type ParentComponent, useContext } from 'solid-js';
import { MissingProviderError } from '../errors/index.ts';
import { formatHashRoute, HOME_ROUTE, parseHashRoute, type Route } from './hash.ts';

export interface RouterValue {
  /** The active route (reactive). */
  readonly route: () => Route;
  /** Navigate by writing the hash — `hashchange` re-affirms the signal. */
  navigate(route: Route): void;
}

const RouterContext = createContext<RouterValue>();

export const RouterProvider: ParentComponent = (props) => {
  const readHash = (): Route =>
    typeof window === 'undefined' ? HOME_ROUTE : parseHashRoute(window.location.hash);
  const [route, setRoute] = createSignal<Route>(readHash());

  if (typeof window !== 'undefined') {
    const onHashChange = (): void => {
      setRoute(readHash());
    };
    window.addEventListener('hashchange', onHashChange);
    onCleanup(() => window.removeEventListener('hashchange', onHashChange));
  }

  const navigate = (next: Route): void => {
    if (typeof window !== 'undefined') {
      window.location.hash = formatHashRoute(next);
    }
    setRoute(next);
  };

  return (
    <RouterContext.Provider value={{ route, navigate }}>{props.children}</RouterContext.Provider>
  );
};

/** Access the router. Throws when used outside {@link RouterProvider}. */
export function useRouter(): RouterValue {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new MissingProviderError('useRouter', 'RouterProvider');
  }
  return ctx;
}
